import type { QueryClient } from '@tanstack/react-query'
import type { OrganizationId } from '@/lib/organization-scoping'
import type { SalesOrderIdInput } from '@/lib/services/sales-orders/sales-orders.types'
import {
  FsnOrderStatusNotice,
  FsnCancelOrderDialog,
} from '@/components/orders/FsnOrderStatusNotice'
import CancellationRefundNotice from '@/components/orders/CancellationRefundNotice'
import { ExtraPaymentRefundNotice } from '@/components/orders/ExtraPaymentRefundNotice'
import Chat from '@/components/chat/order-chat'
import { RecordHistoryMenuItem } from '@/components/audit-log/RecordHistoryMenu'
import {
  AdminHeaderActionButton,
  AdminHeaderIconButton,
  AdminRecordHeader,
  IconActionButton,
} from '@/components/patterns/admin-page'
import { DetailContactCard } from '@/components/patterns/detail-contact-card'
import AssignDesignerForm from '@/components/forms/Orders/AssignDesignerForm'
import EditCardMessageAndSignatureForm from '@/components/forms/Orders/EditCardMessageAndSignatureForm'
import EditFulfillmentDestinationForm from '@/components/forms/Orders/EditFulfillmentDestinationForm'
import EditRecipientDetailsForm from '@/components/forms/Orders/EditRecipientDetailsForm'
import EditSpecialInstructionsForm from '@/components/forms/Orders/EditSpecialInstructionsForm'
import MarkFulfilledForm from '@/components/forms/Orders/MarkFulfilledForm'
import { canMoveFulfilledOrderBack } from '@/components/forms/Orders/fulfillment-workflow'
import FulfillmentTypeBadge from '@/components/orders/FulfillmentTypeBadge'
import { OrderAside } from '@/components/orders/OrderAside'
import { EditOrderPaymentTermsForm } from '@/components/orders/EditOrderPaymentTermsForm'
import { EditOrderPurchaserForm } from '@/components/orders/EditOrderPurchaserForm'
import { default as OrderFulfillmentStatusBadge } from '@/components/orders/OrderFulfillmentStatusBadge'
import OrderPaymentStatusBadge from '@/components/orders/OrderPaymentStatusBadge'
import { CondolencesMemorialCard } from '@/components/orders/CondolencesMemorialCard'
import { getSalesOrderPaymentLedger } from '@/components/orders/sales-order-payments'
import { SalesOrderCancelDialog } from '@/components/orders/SalesOrderCancelDialog'
import { SalesOrderWorkspace } from '@/components/orders/SalesOrderWorkspace'
import {
  SalesOrderRefundAsideContent,
  buildRefundRequestPresentation,
  formatDeliveryFeeFallbackReason,
  formatDeliveryFeeSource,
  formatRefundRequestTimestamp,
  formatRefundsCardSubtitle,
  formatRefundsCardTitle,
  formatSalesOrderPaymentTerms,
  getDiscountRows,
  isRefundRequestSettled,
} from '@/components/orders/SalesOrderMoneyPresentation'
import { getProductLineItemGroups } from '@/components/orders/order-product-line-groups'
import { getOrderProductRows } from '@/components/orders/order-product-rows'
import OrderRefundStatusBadge from '@/components/orders/OrderRefundStatusBadge'
import RefundRequestProgress from '@/components/forms/Refunds/RefundRequestProgress'
import FsnNetworkFulfillmentPanel from '@/components/orders/FsnNetworkFulfillmentPanel'
import PrintOrder from '@/components/orders/PrintOrder'
import {
  ARRANGEMENT_PHOTO_CONTACT_ERROR,
  ARRANGEMENT_PHOTO_NOTIFICATION_INVALIDATE_INTERVAL_MS,
  ARRANGEMENT_PHOTO_SEND_MODE,
  EMPTY_ARRANGEMENT_PHOTOS,
  MAX_ARRANGEMENT_PHOTO_UPLOADS,
  SalesOrderArrangementPhotoDialog,
  SalesOrderArrangementPhotoUploader,
  SalesOrderArrangementPhotosCard,
  arrangementPhotoSendModeNeedsCustomerDestination,
  canMutateArrangementPhotoNotifications,
  canSendMoreAfterArrangementPhotoNotifications,
  defaultArrangementPhotoScheduledDate,
  getArrangementPhotoMediaItems,
  getEditableArrangementPhotoSendMode,
  getPendingScheduledArrangementPhoto,
  hasActiveArrangementPhotoNotification,
  hasArrangementPhotoCustomerDestination,
  hasSendMoreReadyArrangementPhotoNotification,
  hasSentArrangementPhotoNotification,
  isArrangementPhotoSent,
} from '@/components/orders/SalesOrderArrangementPhotos'
import ViewItemDetails from '@/components/orders/ViewItemDetails'
import {
  canAddOrderToDeliveryRoute,
  getDeliveryRouteAction,
  getDeliveryRouteBadgeLabel,
  getDeliveryRouteMenuLabels,
  getDeliveryRoutesSearch,
  getDeliveryShareErrorMessage,
  getSingleActiveDeliveryRoute,
} from '@/components/orders/delivery-share.utils'
import {
  buildDeliveryRouteStopPayload,
  toDeliveryRouteStopInput,
} from '@/components/orders/delivery-route-stops'
import { getDeliveryAddressVerificationState } from '@/components/orders/delivery-address-verification'
import {
  buildDeliveryAddressMapsUrl,
  getSalesOrderSourceSummary,
} from '@/components/orders/sales-order-detail.helpers'
import ScheduledFulfillmentWell from '@/components/orders/ScheduledFulfillmentWell'
import {
  canCollectSalesOrderPayment,
  canCreateSalesOrderRefund,
  canCancelSalesOrder,
  canUpdateSalesOrderFulfillment,
  currentCustomerAllowsFulfillmentBeforePayment,
  getExternalFinancialProviderLabel,
  getSalesOrderFulfillmentDuePresentation,
  getSalesOrderCollectedActivityTotal,
  isCondolencesSalesOrder,
  isExternalFinancialSalesOrder,
  isIncomingFsnNetworkSalesOrder,
  isCancelledSalesOrder,
  shouldShowSalesOrderHeaderFulfillmentStatus,
  usesCancelledPaymentPresentation,
  usesRefundPaymentPresentation,
} from '@/components/orders/sales-order-presentation'
import { Badge, BadgeButton } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DetailCard,
  DetailIconBadge,
  DetailSectionSurface,
  detailPaleIconClass,
  detailSoftWellClass,
  detailSurfaceActionClass,
  getDetailWellClass,
} from '@/components/patterns/detail-page'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { Can, type AppAbility, useAbility, useIsReadOnly } from '@/lib/casl'
import DefaultPending from '@/components/DefaultPending'
import { canChangeSalesOrderItems, canManageSalesOrderField } from '@/lib/sales-order-access'
import dayjs from '@/lib/dayjs'
import { DeliveryServiceabilityStatus } from '@/lib/enums/DeliveryServiceabilityStatus'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { OrganizationType } from '@/lib/enums/OrganizationType'
import { CustomerPaymentTerms } from '@/lib/enums/CustomerPaymentTerms'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import { SalesOrderPaymentStatus } from '@/lib/enums/SalesOrderPaymentStatus'
import { TransactionLineType } from '@/lib/enums/TransactionLineType'
import { RefundRequestStatus } from '@/lib/enums/RefundRequestStatus'
import { adminTitleHead } from '@/lib/admin-route-title'
import { ICONS } from '@/lib/icons'
import {
  getSalesOrderOutcomeReasonLabel,
  outcomeReasonRequiresNote,
} from '@/lib/sales-order-outcome-reasons'
import {
  deliveryShareQueries,
  useCreateDeliveryShareMutation,
  useDeliveryShares,
  useUpdateDeliveryShareMutation,
} from '@/lib/services/delivery-shares/delivery-shares.query'
import {
  getDuplicateSalesOrderCartSearchParams,
  salesOrderArrangementPhotoSearchActions,
  salesOrderAsideViews,
  salesOrderDetailSearchSchema,
  salesOrderSchema,
} from '@/lib/services/sales-orders/sales-order.schema'
import {
  salesOrderQueries,
  salesOrderQueryKeys,
  useCreateSalesOrderArrangementPhotoMutation,
  useDeleteSalesOrderArrangementPhotoGalleryMutation,
  useDuplicateSalesOrderToCartMutation,
  useReplaceSalesOrderArrangementPhotoGalleryMutation,
  useSendCustomerCopyMutation,
  useSalesOrder,
  useSoftDeleteSalesOrderMutation,
  useUpdateSalesOrderMutation,
  type SalesOrderShowData,
} from '@/lib/services/sales-orders/sales-orders.query'
import type {
  SalesOrder as SalesOrderRecord,
  SalesOrderArrangementPhotoSendMode,
  SalesOrderOutcomeReasonInput,
} from '@/lib/services/sales-orders/sales-orders.types'
import type { MediaFile } from '@/lib/services/media/media.types'
import type { LineItemSource } from '@/lib/services/line-items/line-items.types'
import type { DeliveryShare } from '@/lib/services/delivery-shares/delivery-shares.types'
import type { PaymentModalMode } from '@/components/pos/PaymentModal'
import { useOpenSalesOrderEditCartMutation } from '@/lib/services/sales-orders/sales-order-changes.query'
import { useWarmTerminals } from '@/lib/services/terminals/terminals.query'
import { manualTenderLocationId } from '@/lib/services/cash-drawers/cash-drawers.utils'
import { getRemovedItemLines } from '@/lib/pos/order-change'
import { useSalesOrderDeposits } from '@/lib/services/deposits/deposits.query'
import { getSalesOrderDepositAction } from '@/components/orders/sales-order-deposits'
import { userQueries } from '@/lib/services/users/users.query'
import { canAccessUsersInOrganization } from '@/lib/user-access'
import {
  cn,
  formatCalendarDate,
  getErrorMessage,
  getInvoiceLineItemsWithReturnedStatus,
  isRecord,
  normalizeEmailInput,
  parseCurrency,
} from '@/lib/utils'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import { useCopyToClipboard } from '@uidotdev/usehooks'
import {
  BookOpen,
  BookOpenCheck,
  Camera,
  ChevronDown,
  CheckCircle2,
  Copy,
  DollarSignIcon,
  EllipsisVertical,
  GiftIcon,
  Heart,
  Landmark,
  Mail,
  MapPinIcon,
  Pencil,
  PencilLine,
  Phone,
  Route as RouteIcon,
  SendIcon,
  ShoppingBasket,
  Plus,
  ShoppingCart,
} from 'lucide-react'
import {
  lazy,
  Suspense,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  getInitialSalesOrderAsideView,
  useSalesOrderAsideDeepLink,
} from '@/components/orders/useSalesOrderAsideDeepLink'
import { FsnWireInFinancialNotice } from '@/components/orders/FsnWireInFinancialNotice'

export function getSalesOrderAccess(ability: AppAbility) {
  const canManageField = (field: string) => canManageSalesOrderField(ability, field)
  const canDeleteSalesOrders = ability.can('delete', 'sales-orders')

  const canManageDetails = canManageField('details')
  const canManagePayments = canManageField('payments')
  const canManageRefunds = canManageField('refunds')
  const canManageFulfillment = canManageField('fulfillment')
  const canManageCustomerCopy = canManageField('customer-copy')
  const canManageItems = canChangeSalesOrderItems(ability)

  return {
    canManageAnyEditableSection:
      canManageDetails ||
      canManagePayments ||
      canManageRefunds ||
      canManageFulfillment ||
      canManageCustomerCopy ||
      canManageItems ||
      canDeleteSalesOrders,
    canManageCustomerCopy,
    canManageItems,
    canManageDetails,
    canManageFulfillment,
    canManagePayments,
    canManageRefunds,
    canDeleteSalesOrders,
  }
}

const PaymentModal = lazy(() =>
  import('@/components/pos/PaymentModal').then((module) => ({
    default: module.PaymentModal,
  }))
)

export async function loadSalesOrderDetail({
  context,
  params,
}: {
  context: { ability: AppAbility; queryClient: Pick<QueryClient, 'ensureQueryData'> }
  params: { orgId: OrganizationId; salesOrderId: SalesOrderIdInput }
}) {
  if (!context.ability.can('view', 'sales-orders')) throw notFound()

  const salesOrderAccess = getSalesOrderAccess(context.ability)
  const salesOrder = await context.queryClient.ensureQueryData(
    salesOrderQueries.show({
      organizationId: params.orgId,
      salesOrderId: params.salesOrderId,
    })
  )
  const organizationForUserAccess = {
    type:
      Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)
        ? OrganizationType.Global.Value
        : OrganizationType.Florist.Value,
  }
  if (canAccessUsersInOrganization(context.ability, organizationForUserAccess)) {
    void context.queryClient.ensureQueryData(userQueries.index({ organizationId: params.orgId }))
  }

  return {
    salesOrder,
    isReadOnly: !salesOrderAccess.canManageAnyEditableSection,
  }
}

export const salesOrderDetailHead = ({ params }: { params: { salesOrderId: SalesOrderIdInput } }) =>
  adminTitleHead(`Order #${params?.salesOrderId}`, 'Orders')

export const Route = createFileRoute('/_authenticated/$orgId/sales-orders/$salesOrderId')({
  component: SalesOrder,
  params: {
    parse: (params) => salesOrderSchema.pick({ salesOrderId: true }).parse(params),
  },
  validateSearch: (search) => salesOrderDetailSearchSchema.parse(search),
  head: salesOrderDetailHead,
  loader: loadSalesOrderDetail,
})

type ArrangementPhotoUploaderProps = ComponentProps<typeof SalesOrderArrangementPhotoUploader>

function SalesOrderAsideFrame({ children }: { children: ReactNode }) {
  return <div className="mt-5 pl-0 sm:mt-6 sm:pl-1">{children}</div>
}

// The order query shows the order's index row as placeholder data until the full order arrives. This page reads the
// order through suspense after its loader fetched it, so it never sees the row, but the query's type allows it.
function isFullSalesOrder(order: SalesOrderShowData): order is SalesOrderRecord {
  return !('arrangement_photo_status' in order)
}

export function SalesOrder() {
  const { salesOrderId } = Route.useParams()
  const { data: order } = useSalesOrder(salesOrderId)

  if (!isFullSalesOrder(order)) {
    return <DefaultPending />
  }

  return <SalesOrderDetail order={order} />
}

function SalesOrderDetail({ order }: { order: SalesOrderRecord }) {
  const ability = useAbility()
  const salesOrderAccess = getSalesOrderAccess(ability)
  const navigate = useNavigate({ from: '/$orgId/sales-orders/$salesOrderId' })
  const queryClient = useQueryClient()
  const path = usePathHelper()
  const [, copyToClipboard] = useCopyToClipboard()
  const { orgId, salesOrderId } = Route.useParams()
  const organizationId = useRouteOrganizationId()
  const { aside, arrangementPhoto, cancel } = Route.useSearch()
  const canViewSalesReport =
    ability.can('list', 'transactions') &&
    Number(orgId) !== Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)
  const canViewPayouts = ability.can('view', 'payouts')
  const { data: salesOrderDepositMapping } = useSalesOrderDeposits(salesOrderId, {
    enabled: canViewPayouts,
  })
  const depositAction = getSalesOrderDepositAction(salesOrderDepositMapping, salesOrderId)
  const relatedDepositsLink = depositAction
    ? depositAction.deposit
      ? path.deposits.show(depositAction.deposit.id).using({ orgId }).search(depositAction.search)
          .link
      : path.deposits.index().using({ orgId }).search(depositAction.search).link
    : null
  const softDeleteSalesOrder = useSoftDeleteSalesOrderMutation()
  const { formatPhone } = usePhoneFormatter()
  const { isReadOnly } = useIsReadOnly()
  const createDeliveryRoute = useCreateDeliveryShareMutation()
  const updateDeliveryRoute = useUpdateDeliveryShareMutation()
  const duplicateSalesOrderToCart = useDuplicateSalesOrderToCartMutation()
  const openSalesOrderEditCart = useOpenSalesOrderEditCartMutation()
  const sendCustomerCopyMutation = useSendCustomerCopyMutation()
  const createArrangementPhotoMutation = useCreateSalesOrderArrangementPhotoMutation()
  const deleteArrangementPhotoGalleryMutation = useDeleteSalesOrderArrangementPhotoGalleryMutation()
  const replaceArrangementPhotoGalleryMutation =
    useReplaceSalesOrderArrangementPhotoGalleryMutation()
  const updateSalesOrderMutation = useUpdateSalesOrderMutation()
  const [isSendCustomerCopyOpen, setIsSendCustomerCopyOpen] = useState(false)
  const [sendCustomerCopyEmail, setSendCustomerCopyEmail] = useState('')
  const [sendCustomerCopyError, setSendCustomerCopyError] = useState<string | null>(null)
  const [sendCustomerCopySuccessDestination, setSendCustomerCopySuccessDestination] = useState<
    string | null
  >(null)
  const [isArrangementPhotoOpen, setIsArrangementPhotoOpen] = useState(false)
  const [isArrangementPhotoUploaderOpen, setIsArrangementPhotoUploaderOpen] = useState(false)
  const [isManagingArrangementPhotoGallery, setIsManagingArrangementPhotoGallery] = useState(false)
  const [isAddingMoreArrangementPhotos, setIsAddingMoreArrangementPhotos] = useState(false)
  const [arrangementPhotoMediaItems, setArrangementPhotoMediaItems] = useState<MediaFile[]>([])
  const [arrangementPhotoError, setArrangementPhotoError] = useState<string | null>(null)
  const [arrangementPhotoUploadSessionId, setArrangementPhotoUploadSessionId] = useState(0)
  const activeArrangementPhotoUploadSessionRef = useRef(0)
  const arrangementPhotoUploadSessionsRef = useRef(new Set<number>())
  const requestedReadOrderIdRef = useRef<number | null>(null)
  const [arrangementPhotoSendMode, setArrangementPhotoSendMode] =
    useState<SalesOrderArrangementPhotoSendMode>(ARRANGEMENT_PHOTO_SEND_MODE.sendNow)
  const [arrangementPhotoScheduledDate, setArrangementPhotoScheduledDate] = useState(
    defaultArrangementPhotoScheduledDate
  )
  const [paymentModalMode, setPaymentModalMode] = useState<PaymentModalMode | null>(null)
  const [viewItemDetails, setViewItemDetails] = useState<LineItemSource | null>(null)
  const [isFsnCancelOpen, setIsFsnCancelOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState<SalesOrderOutcomeReasonInput | ''>('')
  const [cancelReasonNote, setCancelReasonNote] = useState('')
  const [sendCancellationEmail, setSendCancellationEmail] = useState(true)
  const [addedDeliveryRoute, setAddedDeliveryRoute] = useState<{
    salesOrderId: number
    route: DeliveryShare
  } | null>(null)
  const [loadingDeliveryRouteId, setLoadingDeliveryRouteId] = useState<number | null>(null)
  const [workspaceTab, setWorkspaceTab] = useState('products')

  const cardMessage = order.card_message
  const signature = order.signature
  const specialInstructions = order.special_instructions
  const paymentSnapshot = order?.payment_snapshot ?? null
  const paymentBootstrap = paymentSnapshot?.payment_bootstrap ?? null
  const isCancelled = isCancelledSalesOrder(order)
  const isFulfilled =
    order?.fulfillment_status === SalesOrderFulfillmentStatus.Fulfilled.Value ||
    Boolean(order?.fulfilled_at)
  const paidTotal = getSalesOrderCollectedActivityTotal(order)
  const refundRequests = [...(order?.refund_requests ?? [])].sort(
    (a, b) => dayjs(b?.created_at).valueOf() - dayjs(a?.created_at).valueOf()
  )
  const arrangementPhotos = order?.arrangement_photos ?? EMPTY_ARRANGEMENT_PHOTOS
  const arrangementPhotoNotificationSendMoreReady =
    hasSendMoreReadyArrangementPhotoNotification(arrangementPhotos)
  const arrangementPhotoCustomerNotificationSent =
    hasSentArrangementPhotoNotification(arrangementPhotos)
  const arrangementPhotoMediaCount = arrangementPhotoMediaItems.length
  const editableArrangementPhotoIds = isManagingArrangementPhotoGallery
    ? arrangementPhotos
        .filter((photo) => !isArrangementPhotoSent(photo))
        .map((photo) => photo?.id)
        .filter(Boolean)
    : []
  const canDeleteArrangementPhotoGallery =
    isManagingArrangementPhotoGallery &&
    editableArrangementPhotoIds.length > 0 &&
    canMutateArrangementPhotoNotifications(arrangementPhotos)
  const remainingArrangementPhotoUploadSlots = Math.max(
    MAX_ARRANGEMENT_PHOTO_UPLOADS - arrangementPhotoMediaCount,
    1
  )
  // Something is left to collect (`financial_state.can_collect_payment`: more than a cent, collection open).
  const hasOpenBalance = order.financial_state?.can_collect_payment === true
  // Settled refunds appear as receipt lines; this card stays only while one needs attention.
  const hasRefundInProgress = refundRequests.some(
    (refundRequest) => !isRefundRequestSettled(refundRequest)
  )
  const paymentTermsLabel = formatSalesOrderPaymentTerms(order)
  const paymentDueDateLabel = formatCalendarDate(order?.due_date, 'MMM D, YYYY', 'Due on receipt')
  const canEditOrderDetails = !isReadOnly && salesOrderAccess.canManageDetails && !isCancelled
  const defaultCustomerCopyEmail = String(order?.purchaser?.email ?? '').trim()
  const canCollectPaymentForOrder = canCollectSalesOrderPayment(order)
  const isIncomingFsnOrder = isIncomingFsnNetworkSalesOrder(order)
  const isCondolencesOrder = isCondolencesSalesOrder(order)
  const isExternalFinancialOrder = isExternalFinancialSalesOrder(order)
  const externalProviderLabel = getExternalFinancialProviderLabel(order)
  const showFinancialActionGroup = !isIncomingFsnOrder || isCondolencesOrder
  const canCollectPayment =
    !isReadOnly && salesOrderAccess.canManagePayments && !isCancelled && canCollectPaymentForOrder
  const canEditPaymentTerms = canCollectPayment && hasOpenBalance
  const canEditPurchaser =
    !isReadOnly && salesOrderAccess.canManagePayments && !isCancelled && !isExternalFinancialOrder
  const canCreateRefund =
    !isReadOnly &&
    salesOrderAccess.canManageRefunds &&
    !isCancelled &&
    canCreateSalesOrderRefund(order)
  const canRequestFsnCancellation =
    isIncomingFsnOrder && !isReadOnly && salesOrderAccess.canDeleteSalesOrders && !isCancelled
  const canCancelOrder =
    !isReadOnly &&
    salesOrderAccess.canDeleteSalesOrders &&
    !isCancelled &&
    canCancelSalesOrder(order)
  // Arriving from an order change that took every item off opens Cancel Order, as the menu would.
  const [isCancelOrderOpen, setIsCancelOrderOpen] = useState(
    () => Boolean(cancel) && canCancelOrder
  )
  const handleCancelOrderOpenChange = (open: boolean) => {
    setIsCancelOrderOpen(open)
    if (!open && cancel) {
      void navigate({ search: (prev) => ({ ...prev, cancel: undefined }), replace: true })
    }
  }
  const canManageNetworkFulfillment = !isReadOnly && salesOrderAccess.canManageFulfillment
  const canManageFulfillment = canManageNetworkFulfillment && canUpdateSalesOrderFulfillment(order)
  // Moving a delivered or picked-up order back needs no payment, only moving it forward does.
  const canShowFulfillmentControls =
    canManageFulfillment || (canManageNetworkFulfillment && canMoveFulfilledOrderBack(order))
  const canFulfillBeforePayment = currentCustomerAllowsFulfillmentBeforePayment(order)
  const canSendCustomerCopy =
    !isReadOnly &&
    salesOrderAccess.canManageCustomerCopy &&
    !isCancelled &&
    !isExternalFinancialOrder
  const canUseArrangementPhotos = order?.fulfillment_type !== FulfillmentType.InStore.Value
  const canCreateArrangementPhotos = canManageFulfillment && canUseArrangementPhotos
  const canAddArrangementPhoto = canCreateArrangementPhotos && arrangementPhotos.length === 0
  const canManageArrangementPhotoGallery =
    canManageFulfillment &&
    canUseArrangementPhotos &&
    arrangementPhotos.length > 0 &&
    !arrangementPhotoNotificationSendMoreReady &&
    canMutateArrangementPhotoNotifications(arrangementPhotos)
  const canSendMoreArrangementPhotos =
    canManageFulfillment &&
    canUseArrangementPhotos &&
    arrangementPhotoNotificationSendMoreReady &&
    canSendMoreAfterArrangementPhotoNotifications(arrangementPhotos)
  const canSendArrangementPhotoToCustomer = hasArrangementPhotoCustomerDestination(order)
  const canOpenArrangementPhotoHeaderAction =
    canAddArrangementPhoto || canManageArrangementPhotoGallery || canSendMoreArrangementPhotos
  const showArrangementPhotoCard = canUseArrangementPhotos || arrangementPhotos.length > 0
  const canSaveArrangementPhotoForDelivery =
    canManageFulfillment &&
    order?.fulfillment_type === FulfillmentType.Delivery.Value &&
    !isFulfilled
  const canSaveArrangementPhotoForReadyPickup =
    canManageFulfillment &&
    order?.fulfillment_type === FulfillmentType.Pickup.Value &&
    order?.fulfillment_status !== SalesOrderFulfillmentStatus.ReadyForPickup.Value &&
    !isFulfilled &&
    canSendArrangementPhotoToCustomer
  const arrangementPhotoMutationPending =
    createArrangementPhotoMutation.isPending ||
    deleteArrangementPhotoGalleryMutation.isPending ||
    replaceArrangementPhotoGalleryMutation.isPending
  const canUseArrangementPhotoUploader =
    canUseArrangementPhotos &&
    (isAddingMoreArrangementPhotos || isManagingArrangementPhotoGallery || canAddArrangementPhoto)
  const pendingScheduledArrangementPhoto = getPendingScheduledArrangementPhoto(arrangementPhotos)
  const isSendingMoreArrangementPhotos =
    isAddingMoreArrangementPhotos && arrangementPhotoNotificationSendMoreReady
  const isSendingArrangementPhotoUpdate =
    isSendingMoreArrangementPhotos && arrangementPhotoCustomerNotificationSent
  const arrangementPhotoCustomerUpdateNotice = isSendingMoreArrangementPhotos
    ? isSendingArrangementPhotoUpdate
      ? "When you add more photos, we'll email the customer an updated gallery right away."
      : "When you add more photos, we'll send the customer the latest gallery right away."
    : null
  const arrangementPhotoDialogTitle = isSendingMoreArrangementPhotos
    ? isSendingArrangementPhotoUpdate
      ? 'Send more arrangement photos'
      : 'Send arrangement photos'
    : isManagingArrangementPhotoGallery
      ? 'Edit arrangement photos'
      : 'Add arrangement photo'
  const arrangementPhotoDialogDescription = isSendingMoreArrangementPhotos
    ? isSendingArrangementPhotoUpdate
      ? 'Add finished arrangement photos to send an update to the purchaser.'
      : 'Add finished arrangement photos to send to the purchaser.'
    : isManagingArrangementPhotoGallery
      ? 'Update the photo list and send timing before it goes out.'
      : 'Share a finished arrangement with the purchaser.'
  const canNotifyCustomerOnCancel =
    !isExternalFinancialOrder && !isCancelled && Boolean(defaultCustomerCopyEmail)
  const deliveryRouteAction = getDeliveryRouteAction(order, {
    isReadOnly: !canManageFulfillment,
  })
  const canCreateDeliveryRoute = deliveryRouteAction === 'create'
  const deliveryRoutes = useDeliveryShares({
    enabled: canCreateDeliveryRoute,
  })
  const deliveryAddress = order?.delivery_address || null
  const openBalanceGuidance = canFulfillBeforePayment
    ? order.fulfillment_type === FulfillmentType.Delivery.Value
      ? 'This order can be delivered before payment is collected.'
      : order.fulfillment_type === FulfillmentType.Pickup.Value
        ? 'This order can be picked up before payment is collected.'
        : 'This order can be fulfilled before payment is collected.'
    : order.fulfillment_type === FulfillmentType.Delivery.Value
      ? 'Collect the remaining balance before treating this order as ready for delivery.'
      : order.fulfillment_type === FulfillmentType.Pickup.Value
        ? 'Collect the remaining balance before treating this order as ready for pickup.'
        : 'Collect the remaining balance before handing this order over.'
  const canOpenRecipientAside = canEditOrderDetails
  const canOpenDestinationAside =
    canEditOrderDetails &&
    (order.fulfillment_type === FulfillmentType.Delivery.Value ||
      order.fulfillment_type === FulfillmentType.Pickup.Value)
  const canViewHistory = ability.can('view', 'audit-logs')
  // Global staff check an order's posted journal before QuickBooks reads it.
  const canViewJournal =
    ability.can('access', 'global') && ability.can('view', 'accounting-journal')
  const showOrderManagementMenuItems =
    !isCancelled &&
    (showFinancialActionGroup || canRequestFsnCancellation) &&
    ((!isExternalFinancialOrder && canCreateRefund) || canCancelOrder || canRequestFsnCancellation)
  const showSalesReportLink =
    canViewSalesReport && !isExternalFinancialOrder && Boolean(order.created_at)
  const orderCreatedDay = dayjs(order.created_at)
  const showReportMenuGroup = showSalesReportLink || Boolean(depositAction) || canViewJournal
  const showMoreActionsMenu = canViewHistory || showOrderManagementMenuItems || showReportMenuGroup
  const isReadyToResolveAside = Boolean(order?.id)
  const isReadyToResolveArrangementPhoto = Boolean(order?.id)
  const shouldWarmPaymentModal = canCollectPayment || Boolean(paymentModalMode)
  const shouldWarmReaderPayments =
    shouldWarmPaymentModal && Boolean(paymentBootstrap?.stripe_account)
  const initialAsideView = getInitialSalesOrderAsideView({
    aside,
    canOpenDestinationAside,
    canOpenRecipientAside,
  })
  const [isAsideOpen, setIsAsideOpen] = useState(Boolean(initialAsideView))
  const [view, setView] = useState(initialAsideView ?? '')
  const deliveryAddressVerification = getDeliveryAddressVerificationState(deliveryAddress)
  const availableExistingDeliveryRoutes = useMemo(
    () =>
      (deliveryRoutes.data ?? [])
        .filter((route) => canAddOrderToDeliveryRoute(route, order))
        .sort((left, right) => {
          const leftDate = String(left?.delivery_date ?? '')
          const rightDate = String(right?.delivery_date ?? '')

          return (
            leftDate.localeCompare(rightDate) ||
            getDeliveryRouteMenuLabels(left).primary.localeCompare(
              getDeliveryRouteMenuLabels(right).primary
            )
          )
        }),
    [deliveryRoutes.data, order]
  )
  const currentOrderAddedDeliveryRoute =
    addedDeliveryRoute?.salesOrderId === order.id ? addedDeliveryRoute.route : null
  const singleActiveDeliveryRoute = getSingleActiveDeliveryRoute(order)
  const directDeliveryRoute = currentOrderAddedDeliveryRoute || singleActiveDeliveryRoute

  useWarmTerminals({
    staleTime: 60_000,
    enabled: shouldWarmReaderPayments,
  })

  useEffect(() => {
    if (!hasActiveArrangementPhotoNotification(arrangementPhotos)) {
      return
    }

    const intervalId = window.setInterval(() => {
      void queryClient.invalidateQueries({
        queryKey: salesOrderQueryKeys.show({
          organizationId: orgId,
          salesOrderId,
        }),
      })
    }, ARRANGEMENT_PHOTO_NOTIFICATION_INVALIDATE_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  }, [arrangementPhotos, orgId, queryClient, salesOrderId])

  const DeliveryVerificationIcon = deliveryAddressVerification
    ? (ICONS[deliveryAddressVerification.Icon] ?? null)
    : null
  const headerMetaItems = [
    getSalesOrderSourceSummary(order?.source, order?.created_at, order?.order_source_link),
  ]

  const pickupLocationAddress = order?.location?.address
    ? { ...order.location.address, name: '' }
    : null
  const deliveryAddressMapsUrl = buildDeliveryAddressMapsUrl(deliveryAddress)
  const pickupLocationMapsUrl = buildDeliveryAddressMapsUrl(pickupLocationAddress)
  const sendCustomerEmailTitle = sendCustomerCopySuccessDestination
    ? 'Email on the way'
    : hasOpenBalance
      ? 'Email this order with balance due'
      : 'Email this order'
  const sendCustomerEmailDescription = sendCustomerCopySuccessDestination
    ? hasOpenBalance
      ? `We'll email this order and balance due to ${sendCustomerCopySuccessDestination} shortly.`
      : `We'll email this order to ${sendCustomerCopySuccessDestination} shortly.`
    : hasOpenBalance
      ? defaultCustomerCopyEmail
        ? 'Review the email address below before emailing this order and balance due.'
        : 'Enter the email address where this order and balance due should be sent.'
      : defaultCustomerCopyEmail
        ? 'Review the email address below before emailing this order confirmation.'
        : 'Enter the email address where this order confirmation should be sent.'

  useSalesOrderAsideDeepLink({
    aside,
    canOpenDestinationAside,
    canOpenRecipientAside,
    isReadyToResolveAside,
    navigate,
    setIsAsideOpen,
    setView,
  })

  function handleViewItemDetailsClick(item: LineItemSource) {
    setView('view-item-details')
    setViewItemDetails(item)
    setIsAsideOpen(true)
  }

  async function handleCancelOrder({
    cashDrawerSessionId,
  }: { cashDrawerSessionId?: number | null } = {}) {
    if (outcomeReasonRequiresNote(cancelReason) && !cancelReasonNote.trim()) {
      toast.error('Add a note for Other.')
      return false
    }

    const reasonNote = outcomeReasonRequiresNote(cancelReason) ? cancelReasonNote.trim() : ''

    try {
      const data = await softDeleteSalesOrder.mutateAsync({
        salesOrderId,
        reason: cancelReason || null,
        sendCustomerEmail: sendCancellationEmail,
        reasonNote: reasonNote || null,
        ...(cashDrawerSessionId ? { cashDrawerSessionId } : {}),
      })
      handleCancelOrderOpenChange(false)
      setCancelReason('')
      setCancelReasonNote('')
      const notify =
        'cancellation' in data && data.cancellation.status === 'needs_attention'
          ? toast.warning
          : toast.success
      notify(data.message || 'Order cancelled successfully')

      return true
    } catch (error) {
      console.error('Cancel order error:', error)
      toast.error(getErrorMessage(error) || 'Failed to cancel order')

      return false
    }
  }

  function openSendCustomerCopyDialog() {
    setSendCustomerCopyEmail(defaultCustomerCopyEmail)
    setSendCustomerCopyError(null)
    setSendCustomerCopySuccessDestination(null)
    setIsSendCustomerCopyOpen(true)
  }

  function handleSendCustomerCopyDialogOpenChange(nextOpen: boolean) {
    if (sendCustomerCopyMutation.isPending) {
      return
    }

    setIsSendCustomerCopyOpen(nextOpen)

    if (!nextOpen) {
      setSendCustomerCopyError(null)
      setSendCustomerCopySuccessDestination(null)
      setSendCustomerCopyEmail(defaultCustomerCopyEmail)
    }
  }

  function handleSendCustomerCopy() {
    const destination = sendCustomerCopyEmail.trim()

    if (!destination) {
      setSendCustomerCopyError('Enter an email address before sending this email.')
      return
    }

    setSendCustomerCopyError(null)

    sendCustomerCopyMutation.mutate(
      {
        salesOrderId: order.id,
        email: destination,
      },
      {
        onSuccess: (response) => {
          setSendCustomerCopySuccessDestination(response?.destination ?? destination)
        },
        onError: (error) => {
          setSendCustomerCopyError(getErrorMessage(error) || 'Please try again')
        },
      }
    )
  }

  function resetArrangementPhotoDialog() {
    activeArrangementPhotoUploadSessionRef.current = 0
    arrangementPhotoUploadSessionsRef.current.clear()
    setIsManagingArrangementPhotoGallery(false)
    setIsAddingMoreArrangementPhotos(false)
    setArrangementPhotoMediaItems([])
    setArrangementPhotoError(null)
    setArrangementPhotoUploadSessionId(0)
    setArrangementPhotoSendMode(ARRANGEMENT_PHOTO_SEND_MODE.sendNow)
    setArrangementPhotoScheduledDate(defaultArrangementPhotoScheduledDate())
    setIsArrangementPhotoUploaderOpen(false)
  }

  function openArrangementPhotoDialog(options: { mode?: 'send_more' } = {}) {
    if (!canUseArrangementPhotos) {
      return
    }

    const shouldSendMore = options.mode === 'send_more' || arrangementPhotoNotificationSendMoreReady

    if (shouldSendMore && !canSendMoreArrangementPhotos) {
      return
    }

    if (!shouldSendMore && !canAddArrangementPhoto && !canManageArrangementPhotoGallery) {
      return
    }

    const shouldManageGallery = !shouldSendMore && canManageArrangementPhotoGallery
    const pendingScheduledPhoto = !shouldSendMore ? pendingScheduledArrangementPhoto : null
    const editablePhoto = shouldManageGallery ? arrangementPhotos[0] : null
    const nextSendMode = shouldSendMore
      ? ARRANGEMENT_PHOTO_SEND_MODE.sendNow
      : pendingScheduledPhoto
        ? ARRANGEMENT_PHOTO_SEND_MODE.scheduled
        : getEditableArrangementPhotoSendMode(editablePhoto)

    setIsManagingArrangementPhotoGallery(shouldManageGallery)
    setIsAddingMoreArrangementPhotos(shouldSendMore)
    setArrangementPhotoMediaItems(
      shouldManageGallery ? getArrangementPhotoMediaItems(arrangementPhotos) : []
    )
    setArrangementPhotoError(
      (shouldSendMore || !shouldManageGallery) &&
        arrangementPhotoSendModeNeedsCustomerDestination(nextSendMode) &&
        !canSendArrangementPhotoToCustomer
        ? ARRANGEMENT_PHOTO_CONTACT_ERROR
        : null
    )
    setArrangementPhotoSendMode(nextSendMode)
    setArrangementPhotoScheduledDate(
      pendingScheduledPhoto?.scheduled_send_at
        ? dayjs.utc(pendingScheduledPhoto.scheduled_send_at).format('YYYY-MM-DD')
        : editablePhoto?.scheduled_send_at
          ? dayjs.utc(editablePhoto.scheduled_send_at).format('YYYY-MM-DD')
          : defaultArrangementPhotoScheduledDate()
    )
    setIsArrangementPhotoOpen(true)
  }

  const openArrangementPhotoDeepLink = useEffectEvent(() => {
    openArrangementPhotoDialog()
  })

  useEffect(() => {
    if (!isReadyToResolveArrangementPhoto) {
      return
    }

    if (arrangementPhoto !== salesOrderArrangementPhotoSearchActions.add) {
      return
    }

    if (canOpenArrangementPhotoHeaderAction) {
      // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
      openArrangementPhotoDeepLink()
    }

    void navigate({
      search: (prev) => ({ ...prev, arrangementPhoto: undefined }),
      ignoreBlocker: true,
      replace: true,
    })
  }, [
    arrangementPhoto,
    canOpenArrangementPhotoHeaderAction,
    isReadyToResolveArrangementPhoto,
    navigate,
  ])

  function openSendMoreArrangementPhotosDialog() {
    openArrangementPhotoDialog({ mode: 'send_more' })
  }

  function handleArrangementPhotoDialogOpenChange(nextOpen: boolean) {
    if (arrangementPhotoMutationPending) {
      return
    }

    setIsArrangementPhotoOpen(nextOpen)

    if (!nextOpen) {
      resetArrangementPhotoDialog()
    }
  }

  function resolveArrangementPhotoSendMode(
    nextSendMode: SalesOrderArrangementPhotoSendMode
  ): SalesOrderArrangementPhotoSendMode {
    if (isAddingMoreArrangementPhotos) {
      return ARRANGEMENT_PHOTO_SEND_MODE.sendNow
    }

    return nextSendMode
  }

  function handleArrangementPhotoScheduledDateChange(date: string) {
    setArrangementPhotoScheduledDate(date)
    setArrangementPhotoError((currentError) =>
      currentError === ARRANGEMENT_PHOTO_CONTACT_ERROR ? currentError : null
    )
  }

  function isActiveArrangementPhotoUploadSession(uploadSessionId: number) {
    return uploadSessionId > 0 && activeArrangementPhotoUploadSessionRef.current === uploadSessionId
  }

  // Earlier uploader windows can still finish; only close/error callbacks stay latest-only.
  function canAcceptArrangementPhotoUploadSession(uploadSessionId: number) {
    return uploadSessionId > 0 && arrangementPhotoUploadSessionsRef.current.has(uploadSessionId)
  }

  const handleArrangementPhotoUploaderSuccess: ArrangementPhotoUploaderProps['onUploadSuccess'] = (
    uploadSessionId,
    _,
    response
  ) => {
    if (!canAcceptArrangementPhotoUploadSession(uploadSessionId)) {
      return
    }

    const uploadedMedia = response.body

    if (uploadedMedia) {
      const nextSendMode = resolveArrangementPhotoSendMode(arrangementPhotoSendMode)

      setArrangementPhotoMediaItems((currentItems) => {
        if (currentItems.some((item) => Number(item.id) === Number(uploadedMedia.id))) {
          return currentItems
        }

        return [...currentItems, uploadedMedia].slice(0, MAX_ARRANGEMENT_PHOTO_UPLOADS)
      })
      setArrangementPhotoError(
        arrangementPhotoSendModeNeedsCustomerDestination(nextSendMode) &&
          !canSendArrangementPhotoToCustomer
          ? ARRANGEMENT_PHOTO_CONTACT_ERROR
          : null
      )
    }
  }

  function handleArrangementPhotoUploaderComplete(uploadSessionId: number) {
    if (!isActiveArrangementPhotoUploadSession(uploadSessionId)) {
      return
    }

    setIsArrangementPhotoUploaderOpen(false)
    setIsArrangementPhotoOpen(true)
  }

  function openArrangementPhotoUploader() {
    if (!canUseArrangementPhotoUploader) {
      return
    }

    const nextUploadSessionId = activeArrangementPhotoUploadSessionRef.current + 1
    activeArrangementPhotoUploadSessionRef.current = nextUploadSessionId
    arrangementPhotoUploadSessionsRef.current.add(nextUploadSessionId)
    setArrangementPhotoUploadSessionId(nextUploadSessionId)
    setIsArrangementPhotoOpen(false)
    setIsArrangementPhotoUploaderOpen(true)
  }

  function closeArrangementPhotoUploader(uploadSessionId: number) {
    if (!isActiveArrangementPhotoUploadSession(uploadSessionId)) {
      return
    }

    setIsArrangementPhotoUploaderOpen(false)
    setIsArrangementPhotoOpen(true)
  }

  function handleArrangementPhotoSendModeChange(nextSendMode: SalesOrderArrangementPhotoSendMode) {
    const resolvedSendMode = resolveArrangementPhotoSendMode(nextSendMode)

    setArrangementPhotoSendMode(resolvedSendMode)
    setArrangementPhotoError(
      arrangementPhotoSendModeNeedsCustomerDestination(resolvedSendMode) &&
        !canSendArrangementPhotoToCustomer
        ? ARRANGEMENT_PHOTO_CONTACT_ERROR
        : null
    )
  }

  async function deleteArrangementPhotoGallery() {
    if (!canDeleteArrangementPhotoGallery) {
      setArrangementPhotoError('Arrangement photos cannot be deleted from this order.')
      return
    }

    setArrangementPhotoError(null)

    try {
      await deleteArrangementPhotoGalleryMutation.mutateAsync({
        salesOrderId: order.id,
      })

      toast.success(
        editableArrangementPhotoIds.length > 1
          ? 'Arrangement photos deleted'
          : 'Arrangement photo deleted'
      )
      setIsArrangementPhotoOpen(false)
      resetArrangementPhotoDialog()
    } catch (error) {
      setArrangementPhotoError(getErrorMessage(error) || 'Photo could not be deleted.')
    }
  }

  async function submitArrangementPhoto() {
    if (!canUseArrangementPhotos) {
      setArrangementPhotoError('Arrangement photos are not available for in-store orders.')
      return
    }

    const isReplacingArrangementPhotoGallery = isManagingArrangementPhotoGallery
    const isSendingMoreArrangementPhotoGallery = isAddingMoreArrangementPhotos
    const resolvedSendMode = resolveArrangementPhotoSendMode(arrangementPhotoSendMode)

    if (
      !isReplacingArrangementPhotoGallery &&
      !isSendingMoreArrangementPhotoGallery &&
      !canAddArrangementPhoto
    ) {
      setArrangementPhotoError('Arrangement photos cannot be added to this order.')
      return
    }

    if (isSendingMoreArrangementPhotoGallery && !canSendMoreArrangementPhotos) {
      setArrangementPhotoError('More arrangement photos cannot be sent for this order.')
      return
    }

    if (arrangementPhotoMediaCount === 0) {
      setArrangementPhotoError('Add at least one photo before continuing.')
      return
    }

    if (
      arrangementPhotoSendModeNeedsCustomerDestination(resolvedSendMode) &&
      arrangementPhotoMediaCount > 0 &&
      !canSendArrangementPhotoToCustomer
    ) {
      setArrangementPhotoError(ARRANGEMENT_PHOTO_CONTACT_ERROR)
      return
    }

    if (
      resolvedSendMode === ARRANGEMENT_PHOTO_SEND_MODE.scheduled &&
      !arrangementPhotoScheduledDate
    ) {
      setArrangementPhotoError('Choose a send date before continuing.')
      return
    }

    setArrangementPhotoError(null)

    const mutation = isReplacingArrangementPhotoGallery
      ? replaceArrangementPhotoGalleryMutation
      : createArrangementPhotoMutation

    mutation.mutate(
      {
        salesOrderId: order.id,
        mediaIds: arrangementPhotoMediaItems.flatMap((media) => (media.id ? [media.id] : [])),
        sendMode: resolvedSendMode,
        scheduledSendDate:
          resolvedSendMode === ARRANGEMENT_PHOTO_SEND_MODE.scheduled
            ? arrangementPhotoScheduledDate
            : undefined,
      },
      {
        onSuccess: () => {
          const scheduledDateLabel = formatCalendarDate(
            arrangementPhotoScheduledDate,
            'MMM D, YYYY',
            arrangementPhotoScheduledDate
          )
          const successMessage = isSendingArrangementPhotoUpdate
            ? 'Photo update queued'
            : isReplacingArrangementPhotoGallery
              ? 'Arrangement photos updated'
              : resolvedSendMode === ARRANGEMENT_PHOTO_SEND_MODE.deliveryNotification
                ? arrangementPhotoMediaCount > 1
                  ? 'Photos will send when the order is marked delivered'
                  : 'Photo will send when the order is marked delivered'
                : resolvedSendMode === ARRANGEMENT_PHOTO_SEND_MODE.readyForPickupNotification
                  ? arrangementPhotoMediaCount > 1
                    ? 'Photos will send when the order is marked ready for pickup'
                    : 'Photo will send when the order is marked ready for pickup'
                  : resolvedSendMode === ARRANGEMENT_PHOTO_SEND_MODE.scheduled
                    ? `${arrangementPhotoMediaCount > 1 ? 'Photos' : 'Photo'} scheduled for ${scheduledDateLabel}`
                    : arrangementPhotoMediaCount > 1
                      ? 'Photo notifications queued'
                      : 'Photo notification queued'

          toast.success(successMessage)
          setIsArrangementPhotoOpen(false)
          resetArrangementPhotoDialog()
        },
        onError: (error) => {
          setArrangementPhotoError(getErrorMessage(error) || 'Photo could not be saved.')
        },
      }
    )
  }

  const markOrderRead = useEffectEvent(() => {
    updateSalesOrderMutation.mutate({
      id: salesOrderId,
      read_at: dayjs().toISOString(),
    })
  })
  const shouldMarkOrderRead =
    !ability.can('access', 'global') && salesOrderAccess.canManageDetails && !order.read_at

  useEffect(() => {
    if (!shouldMarkOrderRead || requestedReadOrderIdRef.current === salesOrderId) return

    requestedReadOrderIdRef.current = salesOrderId
    markOrderRead()
  }, [salesOrderId, shouldMarkOrderRead])

  const openRecipientAside = () => {
    setView(salesOrderAsideViews.editRecipient)
    setIsAsideOpen(true)
  }

  const openDestinationAside = () => {
    setView(salesOrderAsideViews.editFulfillmentDestination)
    setIsAsideOpen(true)
  }

  const openRefundAside = () => {
    setView('create-refund')
    setIsAsideOpen(true)
  }

  const openCardMessageAside = () => {
    setView('edit-card-message-and-signature')
    setIsAsideOpen(true)
  }

  const openSpecialInstructionsAside = () => {
    setView('edit-special-instructions')
    setIsAsideOpen(true)
  }

  const invoiceTransaction = order?.document ?? null
  // How a returned item comes back: the first completed refund that credited it says.
  const returnStatusByItem = new Map<number, string>()
  for (const refund of [...(order.refund_requests ?? [])].sort(
    (left, right) => left.id - right.id
  )) {
    if (refund.status !== RefundRequestStatus.Processed.Value) continue
    for (const credited of refund.attribution_totals ?? []) {
      if (
        credited.sales_order_item_id != null &&
        !returnStatusByItem.has(credited.sales_order_item_id)
      ) {
        returnStatusByItem.set(credited.sales_order_item_id, refund.fulfillment_status)
      }
    }
  }
  // Items an order change removed stay on the order, crossed out like returned items.
  const productLineItems = [
    ...getInvoiceLineItemsWithReturnedStatus(order)
      .filter((item) => item.type === TransactionLineType.Product.Value)
      .map((item) => ({
        ...item,
        returnFulfillmentStatus: returnStatusByItem.get(item.sales_order_item_id ?? 0) ?? null,
      })),
    ...getRemovedItemLines(order),
  ]
  const productLineItemRows = getOrderProductRows(getProductLineItemGroups(productLineItems))
  const productsSummaryUsesAlternateSurface =
    productLineItemRows.length > 0 && productLineItemRows.length % 2 === 1
  const invoiceFreightFee = parseCurrency(invoiceTransaction?.freight_fee ?? 0)
  const invoiceTipAmount = parseCurrency(invoiceTransaction?.tip_amount ?? 0)
  const invoiceRelayFee = parseCurrency(invoiceTransaction?.relay_fee ?? 0)
  const discountRows = getDiscountRows(order)
  const activeRouteLabel = getDeliveryRouteBadgeLabel(order)
  const isDeliveryRouteActionPending =
    createDeliveryRoute.isPending ||
    updateDeliveryRoute.isPending ||
    loadingDeliveryRouteId !== null
  const showDeliverySection =
    Boolean(activeRouteLabel) ||
    order.fulfillment_type === FulfillmentType.Delivery.Value ||
    order.delivery_attempt_count > 0
  const deliveryServiceabilityStatus = order?.delivery_serviceability_status ?? null
  const isOutsideAreaDeliveryOrder =
    deliveryServiceabilityStatus === DeliveryServiceabilityStatus.OutsideDeliveryArea.Value
  const canEvaluateForFsnNetworkFulfillment =
    deliveryServiceabilityStatus === null || isOutsideAreaDeliveryOrder
  const isSubmittedFsnNetworkFulfillment = order?.order_fulfillment?.type === 'fsn_network'
  const canPrepareUnsubmittedFsnNetworkFulfillment =
    order.fulfillment_type === FulfillmentType.Delivery.Value &&
    canEvaluateForFsnNetworkFulfillment &&
    !isFulfilled
  const showFsnNetworkFulfillmentPanel =
    !isIncomingFsnOrder &&
    !isCondolencesOrder &&
    (canPrepareUnsubmittedFsnNetworkFulfillment || isSubmittedFsnNetworkFulfillment)
  const showsRefundHeaderStatus = usesRefundPaymentPresentation(order)
  const showsHeaderFulfillmentStatus = shouldShowSalesOrderHeaderFulfillmentStatus(order)
  const rawSourceMetadata = order.order_source_link?.metadata
  const sourceMetadata = isRecord(rawSourceMetadata) ? rawSourceMetadata : {}
  // Source metadata is the source system's own JSON; read amounts and ids only when they are numbers or strings.
  const readSourceMetadataValue = (key: string) => {
    const value = sourceMetadata[key]

    return typeof value === 'number' || typeof value === 'string' ? value : null
  }
  const deliveryRoutesIndexLink = path.deliveryRoutes
    .index()
    .search(getDeliveryRoutesSearch(order)).link
  const deliveryRoutesLink = directDeliveryRoute?.id
    ? path.deliveryRoutes.show(directDeliveryRoute.id).link
    : deliveryRoutesIndexLink
  const deliveryRoutesActionLabel = directDeliveryRoute?.id
    ? 'Open Delivery Route'
    : 'Open Delivery Routes'
  const historyLink = path.salesOrders.link('$salesOrderId/history', salesOrderId)
  const journalLink = path.salesOrders.link('$salesOrderId/journal', salesOrderId)
  const expectedExternalPayout = parseCurrency(order.order_source_link?.fsn_expected_payout ?? 0)
  const condolencesProductsFinancialSummary = isCondolencesOrder
    ? (order.order_source_link?.condolences_summary ?? null)
    : null
  const purchaserPhone = order?.purchaser?.phone ? formatPhone(order.purchaser.phone) : null
  const recipientPhone = order?.recipient_phone ? formatPhone(order.recipient_phone) : null
  const fulfillmentAddress =
    order.fulfillment_type === FulfillmentType.Delivery.Value
      ? order?.delivery_address
      : order.fulfillment_type === FulfillmentType.Pickup.Value
        ? pickupLocationAddress
        : null
  const fulfillmentAddressLines = fulfillmentAddress
    ? [
        order.fulfillment_type === FulfillmentType.Delivery.Value
          ? order.delivery_destination_name
          : null,
        fulfillmentAddress.line1,
        fulfillmentAddress.line2,
        [
          [fulfillmentAddress.city, fulfillmentAddress.state].filter(Boolean).join(', '),
          fulfillmentAddress.postal_code,
        ]
          .filter(Boolean)
          .join(' ')
          .trim(),
        fulfillmentAddress.country && fulfillmentAddress.country !== 'US'
          ? fulfillmentAddress.country
          : null,
      ].filter(Boolean)
    : []
  const fulfillmentMapsUrl =
    order.fulfillment_type === FulfillmentType.Delivery.Value
      ? deliveryAddressMapsUrl
      : pickupLocationMapsUrl
  const fulfillmentSectionTitle =
    order.fulfillment_type === FulfillmentType.Delivery.Value
      ? 'Delivery Destination'
      : order.fulfillment_type === FulfillmentType.Pickup.Value
        ? 'Pickup Location'
        : 'Fulfillment'
  const FulfillmentSummaryIcon =
    order.fulfillment_type === FulfillmentType.Pickup.Value ? ShoppingBasket : MapPinIcon
  const fulfillmentPresentationTone =
    order.fulfillment_type === FulfillmentType.Pickup.Value
      ? 'amethyst'
      : order.fulfillment_type === FulfillmentType.Delivery.Value
        ? 'periwinkle'
        : 'brand'
  // The API totals the order: the document plus the shop fee and its tax its payments were charged.
  const shopFee = parseCurrency(order.financial_state?.shop_fee_total ?? 0)
  const customerTotal = parseCurrency(order.financial_state?.order_total ?? 0)
  const paymentLedger = isExternalFinancialOrder
    ? null
    : getSalesOrderPaymentLedger(order, {
        total: customerTotal,
        termsLabel: paymentTermsLabel,
        dueDateLabel: paymentDueDateLabel,
      })
  const salesOrderPayment = paymentLedger
    ? {
        ...paymentLedger,
        guidance: hasOpenBalance && !isCancelled ? openBalanceGuidance : null,
        onCollectPayment:
          canCollectPayment && hasOpenBalance ? () => setPaymentModalMode('split') : null,
        onEditTerms: canEditPaymentTerms
          ? () => {
              setView('edit-payment-terms')
              setIsAsideOpen(true)
            }
          : null,
      }
    : null
  const deliveryFeeFallbackReason = String(order?.delivery_fee_fallback_reason ?? '').trim()
  const deliveryFeeSource = String(order?.delivery_fee_source ?? '').trim()
  const showDeliveryFeeFallbackIndicator =
    order.fulfillment_type === FulfillmentType.Delivery.Value &&
    (deliveryFeeFallbackReason !== '' || deliveryFeeSource.startsWith('fallback_'))
  const deliveryFeeFallbackSourceLabel = formatDeliveryFeeSource(deliveryFeeSource)
  const deliveryFeeFallbackReasonLabel = formatDeliveryFeeFallbackReason(deliveryFeeFallbackReason)
  const deliveryFeeFallbackTooltip = `${deliveryFeeFallbackSourceLabel}: ${deliveryFeeFallbackReasonLabel}`

  const renderCopyButton = ({
    ariaLabel,
    successMessage,
    value,
  }: {
    ariaLabel: string
    successMessage: string
    value: string | null | undefined
  }) => {
    if (!value) {
      return null
    }

    return (
      <IconActionButton
        icon={Copy}
        label={ariaLabel}
        variant="ghost"
        className="shrink-0 rounded-xl"
        aria-label={ariaLabel}
        onClick={async () => {
          await copyToClipboard(value)
          toast.success(successMessage)
        }}
      />
    )
  }

  const softWellClass = detailSoftWellClass
  const recipientWellClass = getDetailWellClass({ compact: true, tone: 'mint' })
  const scheduledWellClass = getDetailWellClass({
    compact: true,
    // Wells have no brand tone; an in-store or shipped order's well is neutral.
    tone: fulfillmentPresentationTone === 'brand' ? 'neutral' : fulfillmentPresentationTone,
    className:
      order.fulfillment_type === FulfillmentType.Pickup.Value
        ? 'dark:border-selection-border/35 dark:bg-accentSecondary/35'
        : order.fulfillment_type === FulfillmentType.Delivery.Value
          ? 'dark:border-brand-periwinkle/30 dark:bg-accent/45'
          : undefined,
  })
  const destinationWellClass = getDetailWellClass({ tone: 'mint' })
  const personalTouchWellClass = getDetailWellClass({ tone: 'mint' })
  const paleIconClass = detailPaleIconClass
  const showsDeliveryAddressVerificationBadge =
    Boolean(deliveryAddressVerification) &&
    order.fulfillment_type === FulfillmentType.Delivery.Value
  const surfaceActionClass = detailSurfaceActionClass
  const handleCreateDeliveryRoute = () => {
    if (!canCreateDeliveryRoute || isDeliveryRouteActionPending) {
      return
    }

    createDeliveryRoute.mutate(
      {
        stops: [
          {
            source_type: 'sales_order',
            sales_order_id: order.id,
          },
        ],
      },
      {
        onSuccess: (share) => {
          toast.success('Route link created')
          void navigate({ ...path.deliveryRoutes.edit(share.id).link })
        },
        onError: (error) => {
          toast.error('Unable to create route link', {
            description: getDeliveryShareErrorMessage(error),
          })
        },
      }
    )
  }

  const handleAddOrderToDeliveryRoute = async (route: DeliveryShare) => {
    if (!canCreateDeliveryRoute || isDeliveryRouteActionPending || !route?.id) {
      return
    }

    setLoadingDeliveryRouteId(route.id)

    let currentRoute

    try {
      currentRoute = await queryClient.fetchQuery(
        deliveryShareQueries.show({
          organizationId: orgId,
          deliveryShareId: route.id,
        })
      )
    } catch (error) {
      setLoadingDeliveryRouteId(null)
      toast.error('Unable to add order to route', {
        description: getDeliveryShareErrorMessage(error),
      })
      return
    }

    const routeVersion = currentRoute.route_version
    const stops = (currentRoute.stops ?? []).map((stop) =>
      toDeliveryRouteStopInput(buildDeliveryRouteStopPayload(stop))
    )
    const savedStops = stops.filter((stop) => stop !== null)

    if (!routeVersion || savedStops.length !== stops.length) {
      setLoadingDeliveryRouteId(null)
      toast.error('Unable to add order to route', {
        description: 'Open the route to check its stops, then try again.',
      })
      return
    }

    updateDeliveryRoute.mutate(
      {
        deliveryShareId: currentRoute.id,
        route_version: routeVersion,
        preserve_order: true,
        stops: [
          ...savedStops,
          {
            source_type: 'sales_order',
            sales_order_id: order.id,
          },
        ],
      },
      {
        onSuccess: () => {
          setLoadingDeliveryRouteId(null)
          setAddedDeliveryRoute({
            salesOrderId: order.id,
            route: currentRoute,
          })
          toast.success('Order added to delivery route')
        },
        onError: (error) => {
          setLoadingDeliveryRouteId(null)
          toast.error('Unable to add order to route', {
            description: getDeliveryShareErrorMessage(error),
          })
        },
      }
    )
  }

  const renderDeliveryRouteAction = () => {
    if (currentOrderAddedDeliveryRoute) {
      const { primary } = getDeliveryRouteMenuLabels(currentOrderAddedDeliveryRoute)

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <AdminHeaderActionButton
              variant="outline"
              className="border-status-success-border bg-status-success-background text-status-success-foreground hover:brightness-95"
            >
              <CheckCircle2 className="size-4" />
              <span className="max-w-40 truncate">{primary || 'Added to Route'}</span>
              <ChevronDown className="size-4" />
            </AdminHeaderActionButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" size="lg">
            <DropdownMenuItem asChild>
              <Link {...path.deliveryRoutes.show(currentOrderAddedDeliveryRoute.id).link}>
                <RouteIcon className="size-4" />
                Open Route
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link {...deliveryRoutesIndexLink}>
                <RouteIcon className="size-4" />
                Delivery Routes
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }

    if (!deliveryRouteAction) {
      return null
    }

    if (deliveryRouteAction !== 'create') {
      return (
        <AdminHeaderActionButton variant="outline" asChild>
          <Link {...deliveryRoutesLink}>
            <RouteIcon className="size-4" />
            Delivery Routes
          </Link>
        </AdminHeaderActionButton>
      )
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <AdminHeaderActionButton variant="outline" disabled={isDeliveryRouteActionPending}>
            <RouteIcon className="size-4" />
            {isDeliveryRouteActionPending ? 'Updating Route...' : 'Delivery Route'}
            <ChevronDown className="size-4" />
          </AdminHeaderActionButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" size="2xl">
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={handleCreateDeliveryRoute}>
              <Plus className="size-4" />
              <span>Create New Route</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />
          <DropdownMenuLabel>Add to route</DropdownMenuLabel>

          {deliveryRoutes.isLoading ? (
            <DropdownMenuItem disabled>Loading routes...</DropdownMenuItem>
          ) : availableExistingDeliveryRoutes.length > 0 ? (
            <DropdownMenuGroup>
              {availableExistingDeliveryRoutes.map((route) => {
                const { primary, secondary } = getDeliveryRouteMenuLabels(route)

                return (
                  <DropdownMenuItem
                    key={route.id}
                    size="comfortable"
                    className="items-start"
                    onClick={() => handleAddOrderToDeliveryRoute(route)}
                  >
                    <RouteIcon className="mt-0.5 size-4" />
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{primary}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {secondary}
                      </span>
                    </span>
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuGroup>
          ) : (
            <DropdownMenuItem disabled>No matching routes</DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const handleEditOrder = async () => {
    try {
      const response = await openSalesOrderEditCart.mutateAsync({ salesOrderId: order.id })
      await navigate({
        ...path.salesOrders.edit(order.id).search({ cartId: response.cart_id }).link,
      })
    } catch (error) {
      toast.error('Unable to change this order', {
        description: getErrorMessage(error),
      })
    }
  }

  const handleDuplicateOrder = async () => {
    try {
      const response = await duplicateSalesOrderToCart.mutateAsync(order.id)
      await navigate({
        ...path.salesOrders
          .create()
          .search(getDuplicateSalesOrderCartSearchParams(response, order.fulfillment_type)).link,
      })
    } catch (error) {
      toast.error('Unable to duplicate order', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <div className="-mr-4 mb-10">
      <div className="pr-6 pt-1">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)] xl:items-start">
          <div className="contents xl:col-start-1 xl:row-start-1 xl:block xl:min-w-0 xl:space-y-5">
            <div className="order-1 min-w-0">
              <AdminRecordHeader
                icon={ShoppingCart}
                iconTone="brand"
                title={`Order #${order?.id}`}
                layout="workspace"
                titleTrailing={
                  <>
                    {showsRefundHeaderStatus ? (
                      <OrderRefundStatusBadge status={order?.refund_status} size="md" />
                    ) : (
                      <OrderPaymentStatusBadge
                        status={order?.payment_status}
                        cancelledAt={
                          usesCancelledPaymentPresentation(order) ? order?.cancelled_at : null
                        }
                        size="md"
                      />
                    )}
                    {showsHeaderFulfillmentStatus ? (
                      <OrderFulfillmentStatusBadge
                        status={order?.fulfillment_status}
                        cancelledAt={order?.cancelled_at}
                        size="md"
                      />
                    ) : null}
                    <FulfillmentTypeBadge fulfillmentType={order?.fulfillment_type} size="md" />
                  </>
                }
                meta={
                  <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {headerMetaItems.map((item) => (
                      <Typography variant="supporting" key={item}>
                        {item}
                      </Typography>
                    ))}
                  </div>
                }
                actions={
                  <>
                    {canSendCustomerCopy && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AdminHeaderActionButton
                              variant="outline"
                              size="icon"
                              aria-label="Send customer invoice"
                              onClick={openSendCustomerCopyDialog}
                              disabled={sendCustomerCopyMutation.isPending}
                            >
                              <SendIcon className="size-4" />
                            </AdminHeaderActionButton>
                          </TooltipTrigger>
                          <TooltipContent variant="periwinkle">
                            Send customer invoice
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    <PrintOrder
                      orderId={order.id}
                      size="icon"
                      variant="outline"
                      className="size-10 rounded-full bg-card"
                    />
                    {showMoreActionsMenu ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <AdminHeaderIconButton icon={EllipsisVertical} label="More actions" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent size="md" align="end">
                          {showReportMenuGroup ? (
                            <DropdownMenuGroup>
                              {showSalesReportLink ? (
                                <DropdownMenuItem asChild>
                                  <Link
                                    {...path.salesSummary
                                      .show(orderCreatedDay.format('YYYY-MM-DD'))
                                      .using({ orgId })
                                      .search({
                                        // Back to Sales Summary returns to the order's month, not today's.
                                        start_date: orderCreatedDay
                                          .startOf('month')
                                          .format('YYYY-MM-DD'),
                                        end_date: orderCreatedDay
                                          .endOf('month')
                                          .format('YYYY-MM-DD'),
                                        highlight_order_id: order.id,
                                      }).link}
                                  >
                                    <BookOpen className="size-4" />
                                    View Sales Summary
                                  </Link>
                                </DropdownMenuItem>
                              ) : null}
                              {depositAction && relatedDepositsLink ? (
                                <DropdownMenuItem asChild>
                                  <Link {...relatedDepositsLink}>
                                    <Landmark className="size-4" />
                                    {depositAction.label}
                                  </Link>
                                </DropdownMenuItem>
                              ) : null}
                              {canViewJournal ? (
                                <DropdownMenuItem asChild>
                                  <Link {...journalLink}>
                                    <BookOpenCheck className="size-4" />
                                    Journal entries
                                  </Link>
                                </DropdownMenuItem>
                              ) : null}
                            </DropdownMenuGroup>
                          ) : null}
                          {showReportMenuGroup && canViewHistory ? <DropdownMenuSeparator /> : null}
                          <RecordHistoryMenuItem link={historyLink} />
                          {showOrderManagementMenuItems ? (
                            <>
                              {canViewHistory || showReportMenuGroup ? (
                                <DropdownMenuSeparator />
                              ) : null}
                              <DropdownMenuGroup>
                                {!isExternalFinancialOrder ? (
                                  <DropdownMenuItem
                                    disabled={!canCreateRefund}
                                    variant="destructive"
                                    onClick={() => {
                                      if (!canCreateRefund) return
                                      openRefundAside()
                                    }}
                                  >
                                    Create Refund
                                  </DropdownMenuItem>
                                ) : null}
                                <DropdownMenuItem
                                  disabled={!canCancelOrder && !canRequestFsnCancellation}
                                  variant="destructive"
                                  onClick={() => {
                                    if (canRequestFsnCancellation) {
                                      setIsFsnCancelOpen(true)
                                      return
                                    }
                                    if (!canCancelOrder) return
                                    setSendCancellationEmail(true)
                                    setIsCancelOrderOpen(true)
                                  }}
                                >
                                  {isExternalFinancialOrder && !isIncomingFsnOrder
                                    ? 'Request Cancellation'
                                    : 'Cancel Order'}
                                </DropdownMenuItem>
                              </DropdownMenuGroup>
                            </>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </>
                }
                secondaryActions={
                  <div className="flex w-full flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {salesOrderAccess.canManageItems && order.item_changes ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {/* A disabled button cannot show a tooltip; the wrapper explains why. */}
                              <span tabIndex={order.item_changes.available ? -1 : 0}>
                                {/* The order's main change action leads the row; it yields the primary style to Collect
                                    Payment when the order owes money. */}
                                <AdminHeaderActionButton
                                  variant={
                                    !order.item_changes.available
                                      ? 'outline'
                                      : hasOpenBalance && canCollectPayment
                                        ? 'selection'
                                        : 'default'
                                  }
                                  disabled={
                                    !order.item_changes.available ||
                                    openSalesOrderEditCart.isPending
                                  }
                                  onClick={handleEditOrder}
                                >
                                  <PencilLine className="size-4" />
                                  Edit Order
                                </AdminHeaderActionButton>
                              </span>
                            </TooltipTrigger>
                            {order.item_changes.unavailable_reason ? (
                              <TooltipContent variant="periwinkle">
                                {order.item_changes.unavailable_reason}
                              </TooltipContent>
                            ) : null}
                          </Tooltip>
                        </TooltipProvider>
                      ) : null}
                      {!isCancelled ? (
                        <>
                          <AssignDesignerForm order={order} headerAction />
                          {canOpenArrangementPhotoHeaderAction ? (
                            <AdminHeaderActionButton
                              variant="outline"
                              onClick={
                                arrangementPhotoNotificationSendMoreReady
                                  ? openSendMoreArrangementPhotosDialog
                                  : () => openArrangementPhotoDialog()
                              }
                            >
                              <Camera className="size-4" />
                              {arrangementPhotoNotificationSendMoreReady
                                ? 'Send more photos'
                                : arrangementPhotos.length
                                  ? 'Manage Photos'
                                  : 'Add Photo'}
                            </AdminHeaderActionButton>
                          ) : null}
                        </>
                      ) : null}
                      <Can do="create" on="sales-orders">
                        <AdminHeaderActionButton
                          variant="outline"
                          disabled={duplicateSalesOrderToCart.isPending}
                          onClick={handleDuplicateOrder}
                        >
                          <Copy className="size-4" />
                          Copy Order
                        </AdminHeaderActionButton>
                      </Can>
                      {!isCancelled ? (
                        <>
                          {canCollectPayment && (
                            <AdminHeaderActionButton
                              variant={hasOpenBalance ? 'default' : 'outline'}
                              onClick={() => setPaymentModalMode('split')}
                            >
                              <DollarSignIcon className="size-4" />
                              Collect Payment
                            </AdminHeaderActionButton>
                          )}
                          {renderDeliveryRouteAction()}
                        </>
                      ) : null}
                    </div>
                    {!isCancelled && canShowFulfillmentControls ? (
                      <MarkFulfilledForm order={order} fullWidth={false} className="shrink-0" />
                    ) : null}
                  </div>
                }
                after={
                  <div
                    data-testid="sales-order-contact-grid"
                    className="grid gap-px bg-border/45 lg:grid-cols-2"
                  >
                    <DetailSectionSurface tone="amethyst">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <DetailIconBadge icon={GiftIcon} tone="amethyst" />
                          <div className="min-w-0">
                            <Typography variant="eyebrow">Recipient</Typography>
                            <Typography variant="cardTitle" className="mt-2 pb-0">
                              {order.recipient_name || 'No recipient name provided'}
                            </Typography>
                          </div>
                        </div>
                        {canOpenRecipientAside ? (
                          <Button
                            variant="outline"
                            size="icon"
                            className={surfaceActionClass}
                            onClick={openRecipientAside}
                            aria-label="Edit recipient details"
                          >
                            <Pencil className="size-4" />
                          </Button>
                        ) : null}
                      </div>

                      <div className="space-y-2.5">
                        <div className={cn('flex items-center gap-3', recipientWellClass)}>
                          <Phone className={cn(paleIconClass, 'stroke-interaction')} />
                          <div className="min-w-0 flex-1">
                            <span className="sr-only">Recipient phone</span>
                            <p className="truncate text-base/6">
                              {recipientPhone || '(None provided)'}
                            </p>
                          </div>
                          {renderCopyButton({
                            ariaLabel: 'Copy recipient phone',
                            successMessage: 'Phone number copied to clipboard',
                            value: order.recipient_phone,
                          })}
                        </div>

                        <div className={cn('flex items-center gap-3', recipientWellClass)}>
                          <Mail className={cn(paleIconClass, 'stroke-interaction')} />
                          <div className="min-w-0 flex-1">
                            <span className="sr-only">Recipient email</span>
                            <p className="truncate text-base/6">
                              {order.recipient_email || '(None provided)'}
                            </p>
                          </div>
                          {renderCopyButton({
                            ariaLabel: 'Copy recipient email',
                            successMessage: 'Email copied to clipboard',
                            value: order.recipient_email,
                          })}
                        </div>
                      </div>
                    </DetailSectionSurface>

                    <DetailSectionSurface tone="periwinkle">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <DetailIconBadge
                            icon={FulfillmentSummaryIcon}
                            tone={fulfillmentPresentationTone}
                          />
                          <div className="min-w-0">
                            <Typography variant="eyebrow">{fulfillmentSectionTitle}</Typography>
                          </div>
                        </div>
                        {canOpenDestinationAside ? (
                          <Button
                            variant="outline"
                            size="icon"
                            className={surfaceActionClass}
                            onClick={openDestinationAside}
                            aria-label="Edit destination details"
                          >
                            <Pencil className="size-4" />
                          </Button>
                        ) : null}
                      </div>

                      <div className="space-y-2.5">
                        <ScheduledFulfillmentWell
                          fulfillmentType={order.fulfillment_type}
                          fulfillmentDate={order.fulfillment_due_date}
                          fulfillmentPresentation={getSalesOrderFulfillmentDuePresentation(order)}
                          isCancelled={isCancelled}
                          compactWellClassName={scheduledWellClass}
                        />

                        <div
                          className={cn(
                            destinationWellClass,
                            showsDeliveryAddressVerificationBadge && 'relative'
                          )}
                        >
                          <div className="space-y-3">
                            {showsDeliveryAddressVerificationBadge &&
                            deliveryAddressVerification ? (
                              <div className="absolute top-3 right-3">
                                {canOpenDestinationAside ? (
                                  <BadgeButton
                                    variant={deliveryAddressVerification.Variant}
                                    size="control-sm"
                                    shape="control"
                                    onClick={openDestinationAside}
                                    aria-label="Open destination details"
                                  >
                                    {DeliveryVerificationIcon ? (
                                      <DeliveryVerificationIcon
                                        data-testid="verified-address-icon"
                                        className="h-lh"
                                      />
                                    ) : null}
                                    <span>{deliveryAddressVerification.Label}</span>
                                  </BadgeButton>
                                ) : (
                                  <Badge
                                    variant={deliveryAddressVerification.Variant}
                                    size="control-sm"
                                    shape="control"
                                  >
                                    {DeliveryVerificationIcon ? (
                                      <DeliveryVerificationIcon
                                        data-testid="verified-address-icon"
                                        className="h-lh"
                                      />
                                    ) : null}
                                    <span>{deliveryAddressVerification.Label}</span>
                                  </Badge>
                                )}
                              </div>
                            ) : null}

                            <div
                              className={cn(
                                'flex items-center gap-3',
                                showsDeliveryAddressVerificationBadge && 'pr-32'
                              )}
                            >
                              <MapPinIcon
                                className={cn(paleIconClass, 'self-center stroke-interaction')}
                              />

                              <div className="min-w-0 flex-1 text-base/7">
                                {fulfillmentAddressLines.length > 0 && fulfillmentMapsUrl ? (
                                  <a
                                    href={fulfillmentMapsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={
                                      order.fulfillment_type === FulfillmentType.Delivery.Value
                                        ? 'Open delivery destination in maps'
                                        : 'Open pickup location in maps'
                                    }
                                    className="min-w-0 underline-offset-4 hover:underline"
                                  >
                                    {fulfillmentAddressLines.map((line) => (
                                      <p key={line}>{line}</p>
                                    ))}
                                  </a>
                                ) : fulfillmentAddressLines.length > 0 ? (
                                  <div className="min-w-0 text-foreground">
                                    {fulfillmentAddressLines.map((line) => (
                                      <p key={line}>{line}</p>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-muted-foreground">
                                    No destination information has been added yet.
                                  </p>
                                )}
                              </div>
                            </div>

                            {activeRouteLabel ? (
                              <div className="flex flex-wrap items-center gap-2 pl-7">
                                <Badge variant="brand">{activeRouteLabel}</Badge>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </DetailSectionSurface>
                  </div>
                }
              />
            </div>

            {showFsnNetworkFulfillmentPanel ? (
              <div className="order-3 min-w-0">
                <FsnNetworkFulfillmentPanel
                  order={order}
                  isReadOnly={!canManageNetworkFulfillment}
                />
              </div>
            ) : null}

            <div className="order-4 min-w-0">
              <SalesOrderWorkspace
                value={workspaceTab}
                onValueChange={setWorkspaceTab}
                order={order}
                lineItems={productLineItemRows}
                onViewItemDetails={handleViewItemDetailsClick}
                productSummary={{
                  condolences: condolencesProductsFinancialSummary,
                  useAlternateSurface: productsSummaryUsesAlternateSurface,
                  invoice: invoiceTransaction,
                  discounts: discountRows,
                  freightFee: invoiceFreightFee,
                  showDeliveryFeeFallback: showDeliveryFeeFallbackIndicator,
                  deliveryFeeFallbackTooltip,
                  relayFee: invoiceRelayFee,
                  tipAmount: invoiceTipAmount,
                  shopFee,
                  shopFeeLabel: order.florist_fee_label,
                  // Tax as receipts show it (with the shop fee's), the florist's part of it, and the Order total.
                  tax: parseCurrency(
                    order.customer_total_lines?.find((line) => line.key === 'tax')?.amount ?? 0
                  ),
                  floristTax: parseCurrency(order.financial_state.florist_tax_total),
                  total: customerTotal,
                  payment: salesOrderPayment,
                }}
                deliveryPresentation={{
                  showSection: showDeliverySection,
                  activeRouteLabel,
                  routesLink: deliveryRoutesLink,
                  actionLabel: deliveryRoutesActionLabel,
                }}
              />
            </div>
          </div>

          <div className="order-2 grid min-w-0 gap-4 lg:grid-cols-2 xl:col-start-2 xl:row-start-1 xl:order-none xl:block xl:self-start xl:gap-0 xl:space-y-4 xl:sticky xl:top-1">
            {isIncomingFsnOrder ? (
              <>
                <FsnOrderStatusNotice change={order.order_source_link?.fsn_change} />
                <FsnCancelOrderDialog
                  open={isFsnCancelOpen}
                  onOpenChange={setIsFsnCancelOpen}
                  order={order}
                />
                <FsnWireInFinancialNotice
                  cancelled={isCancelled}
                  refusedChange={order.order_source_link?.fsn_refused_change}
                  ended={Boolean(order.order_source_link?.fsn_ended)}
                  expectedPayout={expectedExternalPayout}
                  incomingOrderId={readSourceMetadataValue('fsn_incoming_order_id')}
                />
              </>
            ) : null}

            {isCondolencesOrder ? <CondolencesMemorialCard metadata={sourceMetadata} /> : null}

            {!isCancelled && order.item_changes?.extra_payment ? (
              <ExtraPaymentRefundNotice
                salesOrderId={order.id}
                organizationId={organizationId}
                locationId={manualTenderLocationId(order)}
                extraPayment={order.item_changes.extra_payment}
                canManage={!isReadOnly && salesOrderAccess.canManageRefunds}
                customerEmail={order.purchaser?.email ?? null}
              />
            ) : null}

            {isCancelled && order.cancellation ? (
              <CancellationRefundNotice
                salesOrderId={order.id}
                initialStatus={order.cancellation}
                locationId={order.location?.id}
                canManage={!isReadOnly && salesOrderAccess.canManageRefunds}
              />
            ) : null}

            {hasRefundInProgress ? (
              <Card surface="destructive" className="gap-0 overflow-hidden py-0">
                <CardContent className="space-y-4 p-5">
                  <div className="space-y-2">
                    <Typography variant="eyebrow">Refunds</Typography>
                    <Typography variant="h3" wrap="balance" className="pb-0 tabular-nums">
                      {formatRefundsCardTitle(order.financial_state)}
                    </Typography>
                    <Typography variant="supporting" className="block">
                      {formatRefundsCardSubtitle(refundRequests)}
                    </Typography>
                  </div>
                  <div className="space-y-3">
                    {refundRequests
                      .filter(
                        (request, index) =>
                          index < 3 || request.settlement_status === 'pending_settlement'
                      )
                      .map((refundRequest) => {
                        const reasonLabel =
                          refundRequest.reason_label ??
                          getSalesOrderOutcomeReasonLabel(refundRequest.reason) ??
                          'No reason'
                        const refundPresentation = buildRefundRequestPresentation(refundRequest)
                        const timestamp = formatRefundRequestTimestamp(refundRequest.created_at)
                        const showRefundProgress =
                          salesOrderAccess.canManageRefunds &&
                          refundRequest.settlement_status === 'pending_settlement'
                        const paymentContext = (refundRequest.payment_legs ?? [])
                          .map((leg) => leg.destination_label ?? leg.method)
                          .filter(Boolean)
                          .join(', ')

                        return (
                          <div key={refundRequest.id} className={softWellClass}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <Typography variant="eyebrow" className="block">
                                {refundPresentation.eyebrow}
                              </Typography>
                              {timestamp ? (
                                <Typography variant="label" tone="muted">
                                  {timestamp}
                                </Typography>
                              ) : null}
                            </div>
                            <p
                              className={cn(
                                'mt-2 text-lg font-semibold tabular-nums',
                                refundPresentation.amountClassName
                              )}
                            >
                              {refundPresentation.amountText}
                            </p>
                            <p className="mt-2 text-sm leading-6 text-foreground">
                              <span className="font-medium text-foreground">{reasonLabel}</span>
                              {refundRequest.reason_note ? (
                                <>
                                  {' '}
                                  <span className="text-muted-foreground">·</span>{' '}
                                  {refundRequest.reason_note}
                                </>
                              ) : null}
                            </p>
                            {refundPresentation.statusText && !showRefundProgress ? (
                              <p className={cn('mt-2', refundPresentation.statusClassName)}>
                                {refundPresentation.statusText}
                              </p>
                            ) : null}
                            {paymentContext ? (
                              <p className="mt-2 text-sm text-muted-foreground">{paymentContext}</p>
                            ) : null}
                            {showRefundProgress ? (
                              <RefundRequestProgress
                                refundRequest={refundRequest}
                                salesOrderId={order.id}
                                locationId={manualTenderLocationId(order)}
                              />
                            ) : null}
                          </div>
                        )
                      })}
                  </div>
                </CardContent>
              </Card>
            ) : null}

            <DetailContactCard
              title="Purchaser"
              action={
                canEditPurchaser ? (
                  <Button
                    variant="outline"
                    size="icon"
                    className={surfaceActionClass}
                    aria-label="Edit purchaser"
                    onClick={() => {
                      setView('edit-purchaser')
                      setIsAsideOpen(true)
                    }}
                  >
                    <Pencil className="size-4" />
                  </Button>
                ) : null
              }
              name={order?.purchaser?.name}
              fallbackName="No purchaser name provided"
              nameLinkProps={
                order?.purchaser?.id && order?.purchaser?.name
                  ? path.customers.show(order.purchaser.id).link
                  : null
              }
              phone={purchaserPhone}
              phoneCopyValue={order?.purchaser?.phone}
              email={order?.purchaser?.email}
              emailCopyValue={order?.purchaser?.email}
              accent="purchaser"
            />

            <DetailCard accent="personalTouches" contentClassName="space-y-4 p-5">
              <div className="flex items-start gap-3">
                <DetailIconBadge icon={Heart} tone="brand" />
                <div className="min-w-0">
                  <Typography variant="eyebrow">Personal touches</Typography>
                </div>
              </div>

              <div className="space-y-3">
                <div className={personalTouchWellClass}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <Typography variant="eyebrow">Card Message</Typography>
                      {cardMessage ? (
                        <div className="mt-3 space-y-2">
                          <p className="line-clamp-4 text-[1.0625rem]/7 text-foreground">
                            {cardMessage}
                          </p>
                          {signature ? (
                            <Typography
                              variant="supporting"
                              italic
                              className="block"
                            >{`— ${signature}`}</Typography>
                          ) : null}
                        </div>
                      ) : (
                        <Typography variant="supporting" className="mt-3 block">
                          No card message has been added yet.
                        </Typography>
                      )}
                    </div>
                    {canEditOrderDetails ? (
                      <Button
                        variant="outline"
                        size="icon"
                        className={surfaceActionClass}
                        onClick={openCardMessageAside}
                        aria-label="Edit card message and signature"
                      >
                        <Pencil className="size-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className={personalTouchWellClass}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <Typography variant="eyebrow">Special Instructions</Typography>
                      {specialInstructions ? (
                        <p className="mt-3 line-clamp-5 text-[1.0625rem]/7">
                          {specialInstructions}
                        </p>
                      ) : (
                        <Typography variant="supporting" className="mt-3 block">
                          No special instructions have been added yet.
                        </Typography>
                      )}
                    </div>
                    {canEditOrderDetails ? (
                      <Button
                        variant="outline"
                        size="icon"
                        className={surfaceActionClass}
                        onClick={openSpecialInstructionsAside}
                        aria-label="Edit special instructions"
                      >
                        <Pencil className="size-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            </DetailCard>

            <SalesOrderArrangementPhotosCard
              show={showArrangementPhotoCard}
              order={order}
              arrangementPhotos={arrangementPhotos}
              canAddArrangementPhoto={canAddArrangementPhoto}
              canManageArrangementPhotoGallery={canManageArrangementPhotoGallery}
              canSendMoreArrangementPhotos={canSendMoreArrangementPhotos}
              isFulfilled={isFulfilled}
              isReadyForPickup={
                order?.fulfillment_status === SalesOrderFulfillmentStatus.ReadyForPickup.Value
              }
              formatPhone={formatPhone}
              onManagePhotos={() => openArrangementPhotoDialog()}
              onSendMorePhotos={openSendMoreArrangementPhotosDialog}
            />
          </div>
        </div>

        <OrderAside open={isAsideOpen} onOpenChange={setIsAsideOpen}>
          {view === 'edit-purchaser' && canEditPurchaser && (
            <EditOrderPurchaserForm order={order} closeAside={() => setIsAsideOpen(false)} />
          )}
          {view === 'edit-payment-terms' && canEditPaymentTerms && (
            <EditOrderPaymentTermsForm order={order} closeAside={() => setIsAsideOpen(false)} />
          )}
          {view === 'chat' && (
            <SalesOrderAsideFrame>
              <Chat />
            </SalesOrderAsideFrame>
          )}
          {view === salesOrderAsideViews.editRecipient && (
            <EditRecipientDetailsForm
              initialData={order}
              closeAside={() => setIsAsideOpen(false)}
              salesOrderId={salesOrderId}
            />
          )}
          {view === salesOrderAsideViews.editFulfillmentDestination && (
            <EditFulfillmentDestinationForm
              order={order}
              closeAside={() => setIsAsideOpen(false)}
              salesOrderId={salesOrderId}
            />
          )}
          {view === 'view-item-details' && (
            <SalesOrderAsideFrame>
              <ViewItemDetails item={viewItemDetails} />
            </SalesOrderAsideFrame>
          )}
          {view === 'create-refund' && (
            <SalesOrderAsideFrame>
              <SalesOrderRefundAsideContent
                order={order}
                onCompleted={() => {
                  setIsAsideOpen(false)
                }}
              />
            </SalesOrderAsideFrame>
          )}
          {view === 'edit-special-instructions' && (
            <EditSpecialInstructionsForm
              initialData={order}
              closeAside={() => setIsAsideOpen(false)}
              salesOrderId={salesOrderId}
            />
          )}
          {view === 'edit-card-message-and-signature' && (
            <EditCardMessageAndSignatureForm
              initialData={order}
              closeAside={() => setIsAsideOpen(false)}
              salesOrderId={salesOrderId}
              cardMessage={cardMessage}
              signature={signature}
            />
          )}
        </OrderAside>
        {shouldWarmPaymentModal ? (
          <Suspense fallback={null}>
            <PaymentModal
              mode={paymentModalMode}
              onModeChange={setPaymentModalMode}
              paymentSnapshot={paymentSnapshot}
              paymentBootstrap={paymentBootstrap}
              paymentTarget={{
                kind: 'sales_order',
                salesOrderId: order.id,
                cartId: order.cart_id ?? null,
                customerId: order.purchaser?.id ?? order.purchaser_customer_id ?? null,
              }}
            />
          </Suspense>
        ) : null}
        <Dialog open={isSendCustomerCopyOpen} onOpenChange={handleSendCustomerCopyDialogOpenChange}>
          <DialogContent
            showCloseButton={!sendCustomerCopyMutation.isPending}
            size="compact"
            padding="tight"
            spacing="compact"
          >
            <DialogHeader>
              <DialogTitle>{sendCustomerEmailTitle}</DialogTitle>
              <DialogDescription>{sendCustomerEmailDescription}</DialogDescription>
            </DialogHeader>

            {sendCustomerCopySuccessDestination ? (
              <DialogFooter>
                <Button onClick={() => handleSendCustomerCopyDialogOpenChange(false)}>Done</Button>
              </DialogFooter>
            ) : (
              <>
                <div className="space-y-2">
                  <Typography variant="label" className="block">
                    Send To
                  </Typography>
                  <Input
                    type="email"
                    value={sendCustomerCopyEmail}
                    onChange={(event) => {
                      setSendCustomerCopyEmail(normalizeEmailInput(event.target.value))
                      if (sendCustomerCopyError) {
                        setSendCustomerCopyError(null)
                      }
                    }}
                    placeholder="customer@example.com"
                    autoFocus
                  />
                  <Typography variant="label" tone="muted" className="block">
                    This only changes where this email goes right now. It won&apos;t update the
                    saved customer record.
                  </Typography>
                  {sendCustomerCopyError ? (
                    <Typography variant="label" tone="destructive" className="block">
                      {sendCustomerCopyError}
                    </Typography>
                  ) : null}
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => handleSendCustomerCopyDialogOpenChange(false)}
                    disabled={sendCustomerCopyMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSendCustomerCopy}
                    disabled={sendCustomerCopyMutation.isPending || !sendCustomerCopyEmail.trim()}
                  >
                    {sendCustomerCopyMutation.isPending ? 'Sending...' : 'Send Email'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
        <SalesOrderArrangementPhotoDialog
          open={
            isArrangementPhotoOpen &&
            canUseArrangementPhotos &&
            (isAddingMoreArrangementPhotos ||
              isManagingArrangementPhotoGallery ||
              canAddArrangementPhoto)
          }
          onOpenChange={handleArrangementPhotoDialogOpenChange}
          title={arrangementPhotoDialogTitle}
          description={arrangementPhotoDialogDescription}
          descriptionClassName={isSendingMoreArrangementPhotos ? 'sr-only' : undefined}
          customerUpdateNotice={arrangementPhotoCustomerUpdateNotice}
          mediaItems={arrangementPhotoMediaItems}
          mediaCount={arrangementPhotoMediaCount}
          sendMode={arrangementPhotoSendMode}
          scheduledDate={arrangementPhotoScheduledDate}
          error={arrangementPhotoError}
          isSendingMoreArrangementPhotos={isSendingMoreArrangementPhotos}
          isManagingArrangementPhotoGallery={isManagingArrangementPhotoGallery}
          canSaveArrangementPhotoForDelivery={canSaveArrangementPhotoForDelivery}
          canSaveArrangementPhotoForReadyPickup={canSaveArrangementPhotoForReadyPickup}
          canDeleteGallery={canDeleteArrangementPhotoGallery}
          deletePhotoCount={editableArrangementPhotoIds.length}
          mutationPending={arrangementPhotoMutationPending}
          onAddPhotos={openArrangementPhotoUploader}
          onRemoveMedia={(mediaId) =>
            setArrangementPhotoMediaItems((currentItems) =>
              currentItems.filter((item) => item.id !== mediaId)
            )
          }
          onSendModeChange={handleArrangementPhotoSendModeChange}
          onScheduledDateChange={handleArrangementPhotoScheduledDateChange}
          onDeleteGallery={deleteArrangementPhotoGallery}
          onSubmit={submitArrangementPhoto}
        />
        <SalesOrderArrangementPhotoUploader
          orderId={order.id}
          open={isArrangementPhotoUploaderOpen && canUseArrangementPhotoUploader}
          maxNumberOfFiles={remainingArrangementPhotoUploadSlots}
          uploadSessionId={arrangementPhotoUploadSessionId}
          onRequestClose={closeArrangementPhotoUploader}
          onUploadSuccess={handleArrangementPhotoUploaderSuccess}
          onUploadComplete={handleArrangementPhotoUploaderComplete}
          onUploadError={(uploadSessionId: number) => {
            if (!isActiveArrangementPhotoUploadSession(uploadSessionId)) {
              return
            }

            setArrangementPhotoError('Photo upload failed. Please try again.')
            setIsArrangementPhotoUploaderOpen(false)
            setIsArrangementPhotoOpen(true)
          }}
        />
        <SalesOrderCancelDialog
          open={isCancelOrderOpen}
          onOpenChange={handleCancelOrderOpenChange}
          order={order}
          paidTotal={paidTotal}
          canNotifyCustomerOnCancel={canNotifyCustomerOnCancel}
          sendCustomerEmail={sendCancellationEmail}
          onSendCustomerEmailChange={setSendCancellationEmail}
          cancelReason={cancelReason}
          onCancelReasonChange={setCancelReason}
          cancelReasonNote={cancelReasonNote}
          onCancelReasonNoteChange={setCancelReasonNote}
          onCancelOrder={handleCancelOrder}
          isExternalFinancialOrder={isExternalFinancialOrder}
          externalProviderLabel={externalProviderLabel}
        />
      </div>
    </div>
  )
}
